import type { VoiceAudioInput } from 'agents/voice/react';

const WORKLET_SOURCE = `
class LocalVadProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.phase = 0;
    this.previous = 0;
    this.resampled = [];
    this.frame = [];
    this.levelPeak = 0;
    this.levelTicks = 0;
  }
  process(inputs) {
    const input = inputs[0]?.[0];
    if (!input) return true;
    let energy = 0;
    let crossings = 0;
    for (let i = 0; i < input.length; i++) {
      const sample = input[i];
      energy += sample * sample;
      if ((sample >= 0) !== (this.previous >= 0)) crossings++;
      this.previous = sample;
      this.phase += 16000 / sampleRate;
      if (this.phase >= 1) {
        this.resampled.push(sample);
        this.phase -= 1;
      }
    }
    const rms = Math.sqrt(energy / input.length);
    const zeroCrossingRate = crossings / input.length;
    const speechLike = rms >= 0.012 && zeroCrossingRate >= 0.005 && zeroCrossingRate <= 0.48;
    // Niveau à cadence ~21 ms (8 process @ 48 kHz), pic de la fenêtre.
    // Posté à chaque process (2,7 ms), le barge-in du SDK compterait ses
    // interruptChunks en 8 ms : un simple pic de bruit ou l'écho du
    // haut-parleur coupait la phrase de l'agent en plein milieu (vu en E2E).
    this.levelPeak = Math.max(this.levelPeak, speechLike ? rms : 0);
    this.levelTicks += 1;
    if (this.levelTicks >= 8) {
      this.port.postMessage({ type: 'level', rms: this.levelPeak });
      this.levelPeak = 0;
      this.levelTicks = 0;
    }
    for (const sample of this.resampled) {
      this.frame.push(Math.max(-32768, Math.min(32767, Math.round(sample * 32767))));
      if (this.frame.length === 320) {
        const pcm = new Int16Array(this.frame);
        this.port.postMessage({ type: 'pcm', pcm: pcm.buffer }, [pcm.buffer]);
        this.frame = [];
      }
    }
    this.resampled = [];
    return true;
  }
}
registerProcessor('nexacraft-local-vad', LocalVadProcessor);
`;

/** AudioWorklet VAD heuristic + 20 ms PCM frames, consumed by VoiceClient. */
export class LocalVadAudioInput implements VoiceAudioInput {
  onAudioLevel: ((rms: number) => void) | null = null;
  onAudioData: ((pcm: ArrayBuffer) => void) | null = null;

  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: AudioWorkletNode | null = null;
  private muteGain: GainNode | null = null;
  private moduleUrl: string | null = null;

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    this.context = new AudioContext({ sampleRate: 48000 });
    const module = new Blob([WORKLET_SOURCE], { type: 'text/javascript' });
    this.moduleUrl = URL.createObjectURL(module);
    await this.context.audioWorklet.addModule(this.moduleUrl);
    this.source = this.context.createMediaStreamSource(this.stream);
    this.processor = new AudioWorkletNode(this.context, 'nexacraft-local-vad', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.muteGain = this.context.createGain();
    this.muteGain.gain.value = 0;
    this.processor.port.onmessage = (event: MessageEvent<{ type: string; rms?: number; pcm?: ArrayBuffer }>) => {
      if (event.data.type === 'level') this.onAudioLevel?.(event.data.rms ?? 0);
      if (event.data.type === 'pcm' && event.data.pcm) this.onAudioData?.(event.data.pcm);
    };
    this.source.connect(this.processor);
    this.processor.connect(this.muteGain);
    this.muteGain.connect(this.context.destination);
    await this.context.resume();
  }

  stop(): void {
    this.processor?.port.close();
    this.source?.disconnect();
    this.processor?.disconnect();
    this.muteGain?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    void this.context?.close();
    if (this.moduleUrl) URL.revokeObjectURL(this.moduleUrl);
    this.stream = null;
    this.context = null;
    this.source = null;
    this.processor = null;
    this.muteGain = null;
    this.moduleUrl = null;
  }
}
